import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import { formatBytes } from "@core/utils";
import type { PublicDocumentRequirement } from "../../types";

/** One document to gather — « Pré-auth/Ligne document » (Figma `522:28575`). Read-only: nothing attaches here. */
function DocumentRow({
  requirement,
  locale,
}: {
  requirement: PublicDocumentRequirement;
  locale: string;
}) {
  const { t } = useTranslation("acquire");

  return (
    <li className="flex items-start gap-2.5 py-xs">
      <span aria-hidden className="mt-0.5 shrink-0 text-(--text-tertiary)">
        <Icon name="file-text" size={18} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block wrap-break-word text-sm-medium text-(--text-primary)">
          {requirement.name}
        </span>
        {/* Uppercased for legibility only — the API sends lower-case extensions. */}
        <span className="block text-xs-regular text-(--text-secondary)">
          {requirement.accepted_extensions
            .map((extension) => extension.toUpperCase())
            .join(" · ")}
          {" · "}
          {t("preview.documents.max-size", {
            size: formatBytes(requirement.max_size_bytes, locale),
          })}
        </span>
      </span>
      {/*
        The optional badge is a deliberate reinforcement over the wizard, per the frame's note: here
        the required and optional groups can be separated by a long scroll, so the row carries the
        marker as well as the group heading. The wizard, where they sit together, does not.
      */}
      {requirement.is_required ? null : (
        <span className="shrink-0 rounded-full bg-surface-page px-sm py-0.5 text-xs-medium text-(--text-secondary)">
          {t("preview.documents.optional-badge")}
        </span>
      )}
    </li>
  );
}

/**
 * « Ce qu'il faut apporter » — US-E11-01 step 3.
 *
 * Split into required and optional, both read from the round's frozen requirements. The point of
 * the section is that an applicant can gather the paperwork *before* starting, so the accepted
 * formats and size ceiling are stated here rather than discovered at the upload.
 *
 * The photo note is not decoration either: US-E3-08 ratified that a photograph of a paper document
 * is acceptable, and saying so is what turns a paper CV into a valid CV for someone with no scanner.
 */
export function DocumentsToBring({
  requirements,
  policy,
  locale,
}: {
  requirements: PublicDocumentRequirement[];
  /** `CONFIGURED` | `EXPLICITLY_NONE` — the latter is a round that deliberately asks for nothing. */
  policy: string;
  locale: string;
}) {
  const { t } = useTranslation("acquire");

  const required = requirements.filter((item) => item.is_required);
  const optional = requirements.filter((item) => !item.is_required);

  return (
    <section className="flex flex-col gap-lg border-y border-(--border-default) bg-surface-card px-md pt-lg pb-xl sm:px-xl">
      <div className="flex flex-col gap-xxs">
        <h2 className="text-md-semibold text-(--text-primary)">
          {t("preview.documents.title")}
        </h2>
        <p className="text-sm-regular text-(--text-secondary)">
          {/*
            "No documents" is a real configuration, not an empty state — a round can explicitly
            require none. Rendering that as a blank list would read as missing data.
          */}
          {requirements.length === 0
            ? policy === "EXPLICITLY_NONE"
              ? t("preview.documents.none-required")
              : t("preview.documents.not-published")
            : t("preview.documents.description", {
                count: requirements.length,
                required: required.length,
              })}
        </p>
      </div>

      {required.length > 0 ? (
        <div className="flex flex-col gap-xxs">
          <h3 className="text-xs-semibold tracking-wide text-(--text-tertiary) uppercase">
            {t("preview.documents.required-heading")}
          </h3>
          <ul className="flex flex-col">
            {required.map((requirement) => (
              <DocumentRow
                key={requirement.id}
                requirement={requirement}
                locale={locale}
              />
            ))}
          </ul>
        </div>
      ) : null}

      {optional.length > 0 ? (
        <div className="flex flex-col gap-xxs">
          <h3 className="text-xs-semibold tracking-wide text-(--text-tertiary) uppercase">
            {t("preview.documents.optional-heading")}
          </h3>
          <ul className="flex flex-col">
            {optional.map((requirement) => (
              <DocumentRow
                key={requirement.id}
                requirement={requirement}
                locale={locale}
              />
            ))}
          </ul>
          <p className="text-xs-regular text-(--text-tertiary)">
            {t("preview.documents.optional-note")}
          </p>
        </div>
      ) : null}

      {requirements.length > 0 ? (
        <p className="flex items-start gap-2.5 rounded-lg bg-surface-page px-sm py-2.5 text-sm-regular text-(--text-primary)">
          <span
            aria-hidden
            className="mt-0.5 shrink-0 text-(--feedback-info-icon)"
          >
            <Icon name="info-circle" size={16} />
          </span>
          {t("preview.documents.photo-note")}
        </p>
      ) : null}
    </section>
  );
}
